import React from 'react';
import { DragDropContext, Draggable } from 'react-beautiful-dnd';
import { StrictModeDroppable as Droppable } from './StrictModeDroppable';
import { getFileUrl } from '../../utils/fileUtil';
import { stripMarkdown } from '../../utils/markdownUtil';

const statusAccents = {
  'Todo': 'border-l-gray-400',
  'In Progress': 'border-l-blue-500',
  'Done': 'border-l-green-500',
  'Backlog': 'border-l-gray-400',
  'Cancelled': 'border-l-red-500'
};

const statusBadges = {
  'Todo': 'bg-gray-100 text-gray-700',
  'In Progress': 'bg-blue-100 text-blue-700',
  'Done': 'bg-green-100 text-green-700',
  'Backlog': 'bg-gray-100 text-gray-700',
  'Cancelled': 'bg-red-100 text-red-700'
};

const priorityColors = {
  'Low': { badge: 'bg-green-100 text-green-700', dot: 'bg-green-500' },
  'Medium': { badge: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500' },
  'High': { badge: 'bg-yellow-100 text-yellow-700', dot: 'bg-yellow-500' },
  'Urgent': { badge: 'bg-red-100 text-red-700', dot: 'bg-red-500' }
};

const formatDate = (date) => {
  if (!date) return null;
  const d = new Date(date);
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === tomorrow.toDateString()) return 'Tomorrow';

  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const ListView = ({
  board,
  cardsByColumn,
  sortActive = false,
  onCardClick,
  openAddModal,
  bulkMode = false,
  selectedCards = new Set(),
  onToggleCard = () => {},
  onSelectAll = () => {},
  onReorder = () => {},
  onMoveCard = () => {},
  onToggleComplete = () => {}
}) => {
  const handleListDragEnd = (result) => {
    const { destination, source, draggableId } = result;
    if (!destination) return;
    if (destination.droppableId === source.droppableId && destination.index === source.index) return;

    const destColumn = destination.droppableId;
    const destIndex = destination.index;

    const destCards = (cardsByColumn[destColumn] || []).filter(c => c._id !== draggableId);
    let newOrder;
    const before = destCards[destIndex - 1];
    const after = destCards[destIndex];

    if (!before && !after) {
      newOrder = 1000;
    } else if (!before) {
      newOrder = (after.order ?? 0) - 500;
    } else if (!after) {
      newOrder = (before.order ?? 0) + 1000;
    } else {
      newOrder = ((before.order ?? 0) + (after.order ?? 0)) / 2;
    }

    onReorder({ cardId: draggableId, newOrder, column: destColumn });
  };

  return (
    <div className="bg-white dark:bg-gray-900 rounded-lg shadow-sm overflow-hidden flex-1 flex flex-col border border-gray-200 dark:border-gray-700">
      <DragDropContext onDragEnd={handleListDragEnd}>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0">
              <tr>
                {!bulkMode && <th className="w-8 px-3 py-3"></th>}
                {bulkMode && (
                  <th className="w-8 px-3 py-3">
                    <input type="checkbox" readOnly className="w-4 h-4 accent-blue-600" />
                  </th>
                )}
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400">Task</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400" style={{width: '140px'}}>Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400" style={{width: '120px'}}>Priority</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400" style={{width: '100px'}}>Due Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400" style={{width: '120px'}}>Assigned</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {board.columns.map(column => {
                const columnCards = cardsByColumn[column] || [];
                const allColumnSelected = columnCards.length > 0 && columnCards.every(card => selectedCards.has(card._id));
                const accentClass = statusAccents[column] || statusAccents['Todo'];

                return (
                  <React.Fragment key={column}>
                    <tr className={`border-l-4 ${accentClass} bg-gray-50/50 dark:bg-gray-800/30 hover:bg-gray-100 dark:hover:bg-gray-800/50 transition-colors`}>
                      {!bulkMode && <td className="px-3 py-2" />}
                      {bulkMode && (
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            checked={allColumnSelected}
                            onChange={() => onSelectAll(columnCards.map(c => c._id))}
                            className="w-4 h-4 accent-blue-600 cursor-pointer"
                          />
                        </td>
                      )}
                      <td className="px-4 py-2.5">
                        <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">{column}</span>
                        <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">({columnCards.length})</span>
                      </td>
                      <td colSpan="4" className="px-4 py-2.5 text-right">
                        <button
                          onClick={() => openAddModal(column)}
                          className="text-primary-blue hover:text-primary-blue hover:bg-blue-50 dark:hover:bg-blue-900/20 px-2 py-1 rounded text-xs font-medium transition-colors"
                        >
                          + Add
                        </button>
                      </td>
                    </tr>
                    <Droppable droppableId={column} type={`list-${column}`}>
                      {(provided, snapshot) => (
                        <tbody
                          {...provided.droppableProps}
                          ref={provided.innerRef}
                          className={snapshot.isDraggingOver ? 'bg-blue-50 dark:bg-blue-900/10' : ''}
                        >
                          {columnCards.length === 0 ? (
                            <tr className="hover:bg-gray-50 dark:hover:bg-gray-800/30">
                              <td className="px-3 py-6" />
                              <td colSpan="5" className="px-4 py-6 text-center text-sm text-gray-400 dark:text-gray-500">
                                No tasks
                              </td>
                            </tr>
                          ) : (
                            columnCards.map((card, index) => {
                              const isSelected = selectedCards.has(card._id);
                              let isOverdue = false;
                              if (card.dueDate) {
                                const datePart = typeof card.dueDate === 'string' ? card.dueDate.split('T')[0] : null;
                                if (datePart) {
                                  const [y, m, d] = datePart.split('-').map(Number);
                                  const due = new Date(y, m - 1, d);
                                  const today = new Date();
                                  today.setHours(0, 0, 0, 0);
                                  isOverdue = due < today;
                                }
                              }
                              const prioColor = priorityColors[card.priority] || priorityColors['Medium'];

                              return (
                                <Draggable key={card._id} draggableId={card._id} index={index} isDragDisabled={sortActive}>
                                  {(provided, snapshot) => (
                                    <tr
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      onClick={() => bulkMode ? onToggleCard(card._id) : onCardClick(card._id)}
                                      className={`transition-all group hover:bg-blue-50/50 dark:hover:bg-gray-800/30 cursor-pointer ${
                                        isSelected && bulkMode ? 'bg-blue-100 dark:bg-blue-900/30 border-l-4 border-l-blue-500' : ''
                                      } ${snapshot.isDragging ? 'shadow-lg bg-white dark:bg-gray-800 rounded opacity-95' : ''}`}
                                    >
                                      {!bulkMode && (
                                        <td className="px-3 py-3 text-gray-400 dark:text-gray-600 hover:text-gray-600 dark:hover:text-gray-500" {...provided.dragHandleProps}>
                                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                            <path d="M8 5a2 2 0 11-4 0 2 2 0 014 0zM12 5a2 2 0 11-4 0 2 2 0 014 0zM8 13a2 2 0 11-4 0 2 2 0 014 0zM12 13a2 2 0 11-4 0 2 2 0 014 0z" />
                                          </svg>
                                        </td>
                                      )}
                                      {bulkMode && (
                                        <td className="px-3 py-3">
                                          <input
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={() => onToggleCard(card._id)}
                                            onClick={(e) => e.stopPropagation()}
                                            className="w-4 h-4 accent-blue-600 cursor-pointer"
                                          />
                                        </td>
                                      )}
                                      <td className="px-4 py-3">
                                        <div className="flex items-center gap-3 min-w-0">
                                          <button
                                            onClick={(e) => { e.stopPropagation(); onToggleComplete(card._id, card.completedAt); }}
                                            className={`flex-shrink-0 w-4.5 h-4.5 rounded-full border-2 flex items-center justify-center transition-colors ${
                                              card.completedAt
                                                ? 'bg-green-500 border-green-500 text-white'
                                                : 'border-gray-300 dark:border-gray-500 hover:border-green-400'
                                            }`}
                                            title={card.completedAt ? 'Mark incomplete' : 'Mark complete'}
                                          >
                                            {card.completedAt && (
                                              <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="3">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                              </svg>
                                            )}
                                          </button>
                                          {card.coverPhoto && (
                                            <div className="h-7 w-7 rounded overflow-hidden flex-shrink-0">
                                              <img src={getFileUrl(card.coverPhoto)} alt="" className="h-full w-full object-cover" />
                                            </div>
                                          )}
                                          <div className="min-w-0">
                                            <p className={`text-sm font-medium truncate group-hover:text-primary-blue transition-colors ${card.completedAt ? 'line-through text-gray-400 dark:text-gray-500' : isSelected && bulkMode ? 'text-blue-700 dark:text-blue-300' : 'text-gray-900 dark:text-gray-100'}`}>
                                              {card.title}
                                            </p>
                                            {card.description && (
                                              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{stripMarkdown(card.description)}</p>
                                            )}
                                          </div>
                                        </div>
                                      </td>
                                      <td className="px-4 py-3" style={{width: '140px'}}>
                                        <select
                                          value={card.column}
                                          onChange={(e) => {
                                            e.stopPropagation();
                                            onMoveCard(card._id, e.target.value);
                                          }}
                                          onClick={(e) => e.stopPropagation()}
                                          className={`text-xs font-medium px-2 py-1 rounded border-0 outline-none cursor-pointer transition-colors ${statusBadges[card.column] || statusBadges['Todo']}`}
                                        >
                                          {board.columns.map(col => (
                                            <option key={col} value={col}>{col}</option>
                                          ))}
                                        </select>
                                      </td>
                                      <td className="px-4 py-3" style={{width: '120px'}}>
                                        <div className="flex items-center gap-1.5">
                                          <div className={`w-2 h-2 rounded-full ${prioColor.dot}`} />
                                          <span className={`text-xs font-medium px-2 py-0.5 rounded ${prioColor.badge}`}>
                                            {card.priority || 'Medium'}
                                          </span>
                                        </div>
                                      </td>
                                      <td className="px-4 py-3" style={{width: '100px'}}>
                                        {card.dueDate ? (
                                          <span className={`text-xs font-medium px-2 py-0.5 rounded whitespace-nowrap ${
                                            isOverdue ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                                          }`}>
                                            {formatDate(card.dueDate)}
                                          </span>
                                        ) : (
                                          <span className="text-xs text-gray-400 dark:text-gray-500">--</span>
                                        )}
                                      </td>
                                      <td className="px-4 py-3" style={{width: '120px'}}>
                                        {card.assignedTo && card.assignedTo.length > 0 ? (
                                          <div className="flex items-center gap-1">
                                            {card.assignedTo.slice(0, 2).map(user => (
                                              <div
                                                key={user._id}
                                                className="w-6 h-6 rounded-full bg-primary-blue text-white text-xs font-bold flex items-center justify-center flex-shrink-0"
                                                title={user.name}
                                              >
                                                {user.name.charAt(0).toUpperCase()}
                                              </div>
                                            ))}
                                            {card.assignedTo.length > 2 && (
                                              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">+{card.assignedTo.length - 2}</span>
                                            )}
                                          </div>
                                        ) : (
                                          <span className="text-xs text-gray-400 dark:text-gray-500">Unassigned</span>
                                        )}
                                      </td>
                                    </tr>
                                  )}
                                </Draggable>
                              );
                            })
                          )}
                          {provided.placeholder}
                        </tbody>
                      )}
                    </Droppable>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </DragDropContext>
    </div>
  );
};

export default ListView;